import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import Modal from "../../common/Modal";
import PrimaryButton from "../../common/PrimaryButton";
import OutlinedButton from "../../common/OutlinedButton";
import TextInput from "../../forms/textInput";

export type AutoConsentDecision = "approve" | "reject";

interface IAutoConsentDecisionModalProps {
  open: boolean;
  decision: AutoConsentDecision;
  /** Customers of the client still waiting for consent: an approval approves them. */
  waitingLinks?: number | null;
  /** Shown above the reason so the approver knows which request this is. */
  subtitle?: string;
  handleClose: () => void;
  // eslint-disable-next-line no-unused-vars
  handleConfirm: (reason: string) => void;
}

/**
 * Collects the required reason before approving or rejecting an org's request for automatic consent
 * (auto-consent requests design, phase 1). Approving also approves the client's waiting customers,
 * so the confirmation says how many; a rejection's reason is shown to the org.
 */
const AutoConsentDecisionModal = ({
  open,
  decision,
  waitingLinks,
  subtitle,
  handleClose,
  handleConfirm,
}: IAutoConsentDecisionModalProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      customModalClassName={styles.modal}
      customModalContainerClassName={styles.modalContainer}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {t(`lbl_auto_consent_${decision}_title`)}
        </h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        <p className={styles.description}>
          {decision === "approve"
            ? t("lbl_auto_consent_approve_desc", { count: waitingLinks ?? 0 })
            : t("lbl_auto_consent_reject_desc")}
        </p>

        <TextInput
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("lbl_reason_prompt_placeholder")}
        />

        <div className={styles.actions}>
          <PrimaryButton
            disabled={!reason.trim()}
            onClick={() => handleConfirm(reason.trim())}>
            {t("lbl_confirm")}
          </PrimaryButton>
          <OutlinedButton onClick={handleClose}>
            {t("lbl_cancel")}
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default AutoConsentDecisionModal;
